import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const Sedation = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Sedation Dentistry Fort Worth TX | Anxiety-Free Dental Care | Fort Worth Dental</title>
        <meta name="description" content="Sedation dentistry in Fort Worth TX for anxiety-free dental care. IV sedation, oral sedation, nitrous oxide. Comfortable dental experience. Call (817) 985-3337." />
        <meta name="keywords" content="sedation dentistry fort worth, dental sedation fort worth tx, sleep dentistry, dental anxiety, IV sedation, nitrous oxide" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Sedation Dentistry in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Comfortable, Anxiety-Free Dental Care</p>
            <p className="text-sm mt-4 opacity-90">Dental Sedation | Anxiety-Free Dentistry | Comfort Dentistry</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* Purpose of Sedation Dentistry */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/sedation-new-1.jpg" 
                  alt="Sedation Dentistry" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is The Purpose Of Sedation Dentistry?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Many people are absolutely terrified of the dentist. For people with this fear, sedation dentistry is an option. Sedation dentistry involves the use of medications that help patients get through dental treatments and procedures without anxiety or distress. It is a method in which patients can actually go to the dentist, receive the treatment they need, and come out of the experience with no memories of pain or anxiety.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  At Fort Worth Dental, we understand that dental anxiety is real and can prevent people from getting the care they need. Our sedation dentistry services are designed to make your visit as comfortable and stress-free as possible, allowing you to maintain optimal oral health without fear.
                </p>
              </div>
            </div>
          </section>

          {/* How Does Sedation Resolve My Fears */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  How Does Sedation Dentistry Resolve My Fears?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Sedation dentistry works by using medication to help you relax during dental procedures. Depending on the level of sedation used, you may be awake but very relaxed, or you may be in a sleep-like state with little to no memory of the procedure.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  The medications used in sedation dentistry affect the central nervous system, reducing anxiety and creating a sense of calm. You'll still be able to respond to your dentist's instructions, but you won't feel the stress and fear that might normally accompany a dental visit. Many patients report that time seems to pass very quickly during sedation, and some have no memory of the procedure at all.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Sedation dentistry is particularly beneficial for patients who need extensive dental work, have a strong gag reflex, have difficulty sitting still, or have had traumatic dental experiences in the past. It allows these patients to receive the comprehensive care they need in a comfortable, anxiety-free environment.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/sedation-new-2.jpg" 
                  alt="Comfortable Dental Experience" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Levels of Sedation */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              What Are The Levels Of Sedation Dentistry?
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Fort Worth Dental offers different levels of sedation to meet your individual needs:
            </p>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Minimal Sedation (Nitrous Oxide)</h3>
                <p className="text-gray-700 leading-relaxed">
                  Also known as "laughing gas," nitrous oxide is inhaled through a mask placed over your nose. It helps you relax while remaining fully conscious and able to respond to your dentist. The effects wear off quickly after the mask is removed, allowing you to drive yourself home after your appointment. This is the mildest form of sedation and is ideal for patients with mild anxiety.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Moderate Sedation (Oral Sedation)</h3>
                <p className="text-gray-700 leading-relaxed">
                  Oral sedation involves taking a prescribed medication (usually in pill form) before your appointment. Depending on the dosage, this can range from minimal to moderate sedation. You'll be awake but drowsy and may have little memory of the procedure. You'll need someone to drive you to and from your appointment. This option is popular for patients with moderate anxiety or those undergoing longer procedures.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Deep Sedation and General Anesthesia</h3>
                <p className="text-gray-700 leading-relaxed">
                  With deep sedation or general anesthesia, you are either on the edge of consciousness or completely unconscious. You cannot be easily awakened until the effects of the anesthesia wear off or are reversed with medication. This level of sedation is typically used for extensive procedures or patients with severe dental anxiety. It requires careful monitoring and must be administered by a trained professional. You will need someone to drive you home and stay with you for several hours after the procedure.
                </p>
              </div>
            </div>
          </section>

          {/* Different Types of Oral Sedation */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              What Are The Different Types Of Oral Sedation?
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Oral Sedation</h3>
                <p className="text-gray-700 leading-relaxed">
                  This is a form of sedation where the dentist gives you a pill for you to take the night before and/or just before your procedure. It is the easiest form of sedation to administer. The pill is usually from the family of drugs known as Benzodiazepines. You'll be conscious during your procedure but won't remember much of it.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Intravenous (IV) Conscious Sedation</h3>
                <p className="text-gray-700 leading-relaxed">
                  This is sedation that is delivered by way of an IV. The benefit of IV delivery is that the sedation takes effect very quickly. It also allows the dentist or anesthesiologist to adjust the level of sedation more rapidly. You may or may not be conscious during an IV procedure. You are unlikely to remember very much of the procedure at all. This method requires the services of someone who is very experienced in administering IV sedation and anesthesia.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Nitrous Oxide</h3>
                <p className="text-gray-700 leading-relaxed">
                  Also known as laughing gas, this gas is breathed in through the nose, usually through a rubber mask. This helps to relax the patient and is very safe as well as effective. You'll be awake the whole time and may have an excellent memory of the procedure. Many people compare it to a mild, pleasant intoxication and relaxation.
                </p>
              </div>
            </div>
          </section>

          {/* Safety and Candidacy */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Is Sedation Dentistry Safe?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              When administered by a trained dental professional, sedation dentistry is very safe. Your Fort Worth dentist will review your medical history, current medications, and overall health to determine which type of sedation is appropriate for you. During the procedure, your vital signs will be monitored to ensure your safety.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              However, sedation dentistry may not be suitable for everyone. Patients who are pregnant, have certain medical conditions, or take specific medications may not be good candidates. Your dentist will discuss all options with you during your consultation and help you make an informed decision about the best approach for your dental care.
            </p>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Sedation Dentistry?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team is trained in safe sedation techniques to ensure your comfort throughout your dental visit.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Certified Sedation</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Sedation Dentistry Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about sedation dentistry in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Related Blog Post - Internal Link for SEO */}
          <section className="mb-16 bg-amber-50 border border-amber-200 p-8 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">📖 Complete Guide to Sedation Dentistry</h2>
            <p className="text-gray-700 mb-4">
              Nervous about your dental visit? Learn about all your sedation options including IV sedation, oral sedation, and nitrous oxide.
            </p>
            <a 
              href="/blogs/sedation-dentistry-fort-worth-anxious-patients" 
              className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-800"
            >
              Read: Sedation Dentistry Options for Anxious Patients →
            </a>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Sedation Dentistry Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Sedation;